import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import apiClient from './api';
import { Container, Typography, Box, Avatar, Chip, CircularProgress, Rating, Button } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { glass, brand } from './theme';

const REVIEW_SUB_LABELS = [
    ['quality_rating', 'کیفیت کار'],
    ['punctuality_rating', 'وقت‌شناسی'],
    ['behavior_rating', 'رفتار'],
    ['cleanliness_rating', 'نظافت'],
];

function StatCard({ title, value, icon }) {
    return (
        <Box sx={{ ...glass.card, p: 2.5, display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            {icon}
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary">{title}</Typography>
            </Box>
        </Box>
    );
}

function ProviderPublicProfile() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await apiClient.get(`/api/providers/${userId}/`);
                setProfile(response.data);
            } catch (error) {
                console.error("Failed to fetch profile", error);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [userId]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress size={50} /></Box>;
    }

    if (!profile) {
        return <Typography variant="h5" sx={{ textAlign: 'center', mt: 5 }}>پروفایل مورد نظر یافت نشد.</Typography>;
    }

    const reviews = Array.isArray(profile.reviews) ? profile.reviews : [];
    const specialtiesList = (profile.specialties || '').split(',').map(spec => spec.trim()).filter(Boolean);

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%' }}>
            <Box sx={{ background: `linear-gradient(180deg, ${brand.primaryDeep} 0%, ${brand.primary} 160%)`, color: brand.heroText, pt: 4, pb: 10, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', top: -140, insetInlineEnd: -80 }} />
                <Container maxWidth="md" sx={{ position: 'relative' }}>
                    <Button onClick={() => navigate(-1)} startIcon={<ArrowForwardIcon />} size="small" sx={{ mb: 2, color: brand.heroText, opacity: .85 }}>
                        بازگشت
                    </Button>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2.5, textAlign: { xs: 'center', sm: 'start' } }}>
                        <Avatar src={profile.avatar} sx={{ width: 110, height: 110, border: '3px solid rgba(255,255,255,0.25)', fontSize: '2.2rem', fontWeight: 800 }}>
                            {(profile.full_name || 'ه')[0]}
                        </Avatar>
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                {profile.full_name}
                                {profile.is_verified_by_admin && <VerifiedIcon sx={{ color: brand.accent, fontSize: 28 }} titleAccess="تایید شده" />}
                            </Typography>
                            <Typography sx={{ mt: 1, color: 'rgba(245,243,234,.8)' }}>
                                {profile.work_experience_years} سال سابقه کار
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ mt: -6, pb: 6 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <StatCard title="میانگین امتیاز" value={Number(profile.average_rating || 0).toFixed(1)} icon={<StarIcon sx={{ fontSize: 36, color: brand.accent }} />} />
                    <StatCard title="کارهای موفق" value={profile.completed_jobs_count} icon={<WorkHistoryIcon sx={{ fontSize: 36, color: brand.primary }} />} />
                </Box>

                <Box sx={{ ...glass.card, p: { xs: 2.5, md: 3.5 }, mb: 2.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>درباره من</Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                        {profile.bio || "بیوگرافی هنوز ثبت نشده است."}
                    </Typography>

                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>تخصص‌ها</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {specialtiesList.length > 0 ? (
                            specialtiesList.map(spec => <Chip key={spec} label={spec} sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 700 }} />)
                        ) : (
                            <Typography variant="body2" color="text.secondary">هیچ تخصصی ثبت نشده است.</Typography>
                        )}
                    </Box>
                </Box>

                <Box sx={{ ...glass.card, p: { xs: 2.5, md: 3.5 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>نظرات خانواده‌ها ({reviews.length.toLocaleString('fa-IR')})</Typography>
                    {reviews.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {reviews.map((review, i) => (
                                <Box key={review.id} sx={{ pb: 2.5, borderBottom: i < reviews.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: .75 }}>
                                        <Rating value={review.rating} readOnly size="small" />
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{review.reviewer_name}</Typography>
                                    </Box>
                                    {review.comment && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{review.comment}</Typography>}
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {REVIEW_SUB_LABELS.map(([key, label]) => (
                                            <Chip key={key} size="small" variant="outlined" label={`${label}: ${review[key]}`} sx={{ fontSize: '.7rem' }} />
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography color="text.secondary">هنوز هیچ نظری برای این همیار ثبت نشده است.</Typography>
                    )}
                </Box>
            </Container>
        </Box>
    );
}

export default ProviderPublicProfile;
